import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import QuotationMarkIcon from '@atlaskit/icon/core/quotation-mark';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Text } from '@atlaskit/primitives/compiled/text';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import { ProfileCardWrapper } from '@confluence/profile/entry-points/ProfileCardWrapper.ts';
import { ProfileLink } from '@confluence/profile/entry-points/ProfileLinkComponent.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { i18n } from '../blogPostsMacroi18n.ts';
import {
	formatSmartDate,
	getAuthorAccountId,
	getAuthorName,
	getContentUrl,
	getCreatedDate,
	getSpaceUrl,
} from '../utils/blogPostDataUtils.ts';
import type { BlogPostNode } from '../blogPostMacroTypes.ts';

type TitlesListProps = {
	nodes: readonly BlogPostNode[];
};

const styles = cssMap({
	listContainer: {
		paddingBlockEnd: token('space.075'),
	},
	subHeading: {
		borderBlockEnd: `${token('border.width')} solid ${token('color.border')}`,
		color: token('color.text.subtlest'),
	},
	titlesUl: {
		padding: token('space.0'),
		listStyleType: 'none',
	},
	blogItem: {
		borderBlockEnd: `${token('border.width')} solid ${token('color.border')}`,
		paddingBlock: token('space.100'),
		paddingInline: token('space.075'),
		marginBlockStart: token('space.0'),
		overflow: 'hidden',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	titleRow: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: token('space.100'),
		flexWrap: 'wrap',
	},
	leftMeta: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: token('space.050'),
		flexGrow: 1,
		flexBasis: '320px',
		minWidth: '0px',
		flexWrap: 'wrap',
	},
	rightMeta: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		gap: token('space.100'),
		marginInlineStart: 'auto',
		flexWrap: 'wrap',
	},
	blogIcon: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
		color: token('color.text.subtle'),
	},
	titleLinkGroup: {
		display: 'flex',
		alignItems: 'baseline',
		gap: token('space.100'),
		minWidth: '0px',
		maxWidth: '100%',
	},
	titleTextGroup: {
		display: 'block',
		minWidth: '0px',
		overflowWrap: 'anywhere',
	},
	titleAnchor: {
		overflowWrap: 'anywhere',
	},
	subtleAnchor: {
		color: token('color.text.subtlest'),
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'underline',
		},
	},
});

export const TitlesList = ({ nodes }: TitlesListProps): React.JSX.Element => {
	const intl = useIntl();
	const { timeZone: userTimeZone, tenantTimeZone } = useSessionData();
	const timeZone = userTimeZone || tenantTimeZone || undefined;

	return (
		<div css={styles.listContainer} data-testid="blog-posts-titles-list">
			<div css={styles.subHeading}>
				<Heading size="xsmall" as="h4">
					<FormattedMessage {...i18n.subHeading} />
				</Heading>
			</div>
			<Box xcss={styles.titlesUl}>
				{nodes.map((node) => {
					const content = node.content;
					if (!content) {
						return null;
					}

					const createdDate = getCreatedDate(node);
					const authorName = getAuthorName(node);
					const authorAccountId = getAuthorAccountId(node);

					return (
						<Box key={content.id} xcss={styles.blogItem} testId="blog-post-item">
							<div css={styles.titleRow}>
								<div css={styles.leftMeta}>
									<span css={styles.titleLinkGroup}>
										<span css={styles.blogIcon}>
											<QuotationMarkIcon label="" color="currentColor" />
										</span>
										<span css={styles.titleTextGroup}>
											<Anchor href={getContentUrl(node)} xcss={styles.titleAnchor}>
												{content.title}
											</Anchor>{' '}
											<Text as="span" color="color.text.subtlest">
												{intl.formatMessage(i18n.createdBy)}
											</Text>{' '}
											<ProfileCardWrapper
												userId={authorAccountId}
												fullName={authorName}
												position="bottom-start"
											>
												<ProfileLink
													userId={authorAccountId}
													fullName={authorName}
													behavior="link"
													isUnderlinedByDefault={false}
													style={{
														// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- style overrides to match legacy macro subtle link styling
														color: token('color.text.subtlest'),
													}}
												/>
											</ProfileCardWrapper>
										</span>
									</span>
								</div>
								<div css={styles.rightMeta}>
									{Boolean(content.space?.name) && (
										<Anchor
											href={getSpaceUrl(node)}
											xcss={styles.subtleAnchor}
											testId="blog-post-space-link"
										>
											{content.space?.name}
										</Anchor>
									)}
									{Boolean(createdDate) && (
										<Text color="color.text.subtlest">
											{formatSmartDate(createdDate, intl, timeZone)}
										</Text>
									)}
								</div>
							</div>
						</Box>
					);
				})}
			</Box>
		</div>
	);
};
